import React, { useState } from 'react';
import { Eye, EyeOff, ChevronLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// QR Code Image Import - Ye aapke src/assets/qr.jpeg ko sahi se load karega
import qrImage from '../../assets/qr.jpeg';

const ViewAccount = ({ userData }: { userData?: any }) => {
  const navigate = useNavigate();
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [activeTab, setActiveTab] = useState<'Summary' | 'Transactions'>('Summary');
  
  // Modal toggle state for Add Money
  const [showQrModal, setShowQrModal] = useState(false);

  if (!userData) return null;

  // Format values
  const balance = userData.wallet_balance ? parseFloat(userData.wallet_balance).toFixed(2) : '0.00';
  const maskedAccount = userData.account_number 
    ? 'X'.repeat(Math.max(0, userData.account_number.length - 4)) + userData.account_number.slice(-4) 
    : 'XXXXXXXXXXXX';

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-10 relative font-[Urbanist]">
      
      {/* Header Back Button */}
      <button onClick={() => navigate('/account/dashboard')} className="flex items-center gap-2 text-[#64748b] hover:text-[#0f172a] transition-colors font-bold text-lg mb-2">
        <ChevronLeft size={24} strokeWidth={2.5} /> Accounts
      </button>

      {/* Main Account Card - Premium Dark Theme matching Dashboard */}
      <div className="relative overflow-hidden rounded-[30px] bg-[#0f172a] p-8 shadow-xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 right-10 w-40 h-40 bg-[#3b82f6]/20 rounded-full blur-xl translate-y-1/4"></div>

        <div className="relative z-10 flex justify-between items-start mb-6">
          <div>
            <p className="text-white/80 font-bold text-xs uppercase tracking-wider mb-1">Account Number</p>
            <div className="flex items-center gap-3">
              <p className="text-2xl font-black text-white tracking-widest">
                {showAccountInfo ? userData.account_number : maskedAccount}
              </p>
              <button onClick={() => setShowAccountInfo(!showAccountInfo)} className="text-white/80 hover:text-white transition-colors bg-white/5 p-2 rounded-[12px] hover:bg-white/20">
                {showAccountInfo ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <p className="bg-white/10 px-3 py-1 rounded-full text-white/90 font-bold text-[10px] uppercase tracking-wider">Savings A/C</p>
        </div>

        <div className="relative z-10 mb-8">
          <p className="text-[#94a3b8] font-bold text-xs uppercase tracking-wider mb-1">Available Balance</p>
          <h2 className="text-4xl md:text-5xl font-black text-white">
            ₹{balance}
          </h2>
        </div>

        {/* Buttons Section */}
        <div className="relative z-10 flex gap-4 mt-2">
          <button className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-[16px] border border-white/10 transition-colors text-sm">
            Manage Account
          </button>
          <button 
            onClick={() => setShowQrModal(true)} 
            className="flex-1 bg-[#3b82f6] hover:bg-[#2563eb] text-white font-black py-3 rounded-[16px] transition-colors text-sm shadow-[0_8px_20px_rgb(59,130,246,0.3)]"
          >
            Add Money
          </button>
        </div>
      </div>

      {/* Details Section */}
      <div className="bg-white rounded-[30px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#f1f5f9]">
        
        {/* Tabs */}
        <div className="flex bg-[#f8fafc] p-2 m-4 rounded-[20px] border border-[#f1f5f9]">
          <button 
            onClick={() => setActiveTab('Summary')}
            className={`flex-1 py-3 text-sm font-bold rounded-[14px] transition-all ${activeTab === 'Summary' ? 'bg-white text-[#0f172a] shadow-sm' : 'text-[#64748b] hover:text-[#0f172a]'}`}
          >
            Summary
          </button>
          <button 
            onClick={() => setActiveTab('Transactions')}
            className={`flex-1 py-3 text-sm font-bold rounded-[14px] transition-all ${activeTab === 'Transactions' ? 'bg-white text-[#0f172a] shadow-sm' : 'text-[#64748b] hover:text-[#0f172a]'}`}
          >
            Transactions
          </button>
        </div>

        {activeTab === 'Summary' && (
          <div className="p-6 md:p-8 space-y-8">
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm md:text-base border-b border-[#f1f5f9] pb-3">
                <span className="text-[#64748b] font-bold">Available Balance</span>
                <span className="text-[#0f172a] font-black text-lg">₹{balance}</span>
              </div>
              <div className="flex justify-between items-center text-sm md:text-base border-b border-[#f1f5f9] pb-3">
                <span className="text-[#64748b] font-bold">Hold/Lien Amount</span>
                <span className="text-[#0f172a] font-bold">₹0.00</span>
              </div>
              <div className="flex justify-between items-center text-sm md:text-base border-b border-[#f1f5f9] pb-3">
                <span className="text-[#64748b] font-bold">Uncleared Balance</span>
                <span className="text-[#0f172a] font-bold">₹0.00</span>
              </div>
              <div className="flex justify-between items-center text-sm md:text-base pb-2">
                <span className="text-[#64748b] font-bold">MOD Balance</span>
                <span className="text-[#0f172a] font-bold">₹0.00</span>
              </div>
            </div>

            <div className="pt-6 border-t border-dashed border-[#cbd5e1]">
              <p className="text-[11px] text-[#94a3b8] uppercase font-bold tracking-widest mb-1">Account Description</p>
              <p className="text-[#3b82f6] font-black uppercase tracking-wide text-sm mb-6">LOTUS SAVING BANK-ADHAR- CHQ</p>
              
              <div className="grid grid-cols-2 gap-y-6">
                <div className="border-b border-dashed border-[#e2e8f0] pb-4">
                  <p className="text-[11px] text-[#94a3b8] uppercase font-bold tracking-widest mb-1">Currency</p>
                  <p className="text-[#0f172a] font-black text-sm">Rupees</p>
                </div>
                <div className="border-b border-dashed border-[#e2e8f0] pb-4">
                  <p className="text-[11px] text-[#94a3b8] uppercase font-bold tracking-widest mb-1">Mode of Operation</p>
                  <p className="text-[#0f172a] font-black text-sm">Single</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#94a3b8] uppercase font-bold tracking-widest mb-1">Rate of Interest</p>
                  <p className="text-[#0f172a] font-black text-sm">2.50%</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#94a3b8] uppercase font-bold tracking-widest mb-1">Nominee(s)</p>
                  <button className="text-[#3b82f6] hover:text-[#2563eb] font-black text-sm border-b-2 border-[#bfdbfe] pb-0.5 transition-colors">View Details</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Transactions' && (
          <div className="p-10 text-center text-[#64748b]">
            <p className="mb-6 font-bold">Switch to the main Transactions tab for detailed ledger view.</p>
            <button onClick={() => navigate('/account/transactions')} className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-black py-3 px-8 rounded-[16px] transition-colors">Go to Transactions</button>
          </div>
        )}
      </div>

      {/* ADD MONEY QR MODAL - Light Theme */}
      {showQrModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0f172a]/60 backdrop-blur-sm p-4">
          <div className="bg-white border border-[#f1f5f9] rounded-[30px] p-8 max-w-sm w-full shadow-2xl relative text-center">
            
            {/* Close Button */}
            <button 
              onClick={() => setShowQrModal(false)} 
              className="absolute top-4 right-4 text-[#64748b] hover:text-[#0f172a] bg-[#f8fafc] hover:bg-[#f1f5f9] p-2 rounded-full transition-colors"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
            
            <h3 className="text-xl font-black text-[#0f172a] mb-6">Add Funds via UPI</h3>
            
            {/* QR Code Container Using the Imported Image */}
            <div className="bg-white p-3 rounded-[20px] inline-block mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#f1f5f9]">
              <img 
                src={qrImage} 
                alt="Scan to Pay" 
                className="w-48 h-48 object-cover rounded-xl"
              />
            </div>
            
            {/* Instructions */}
            <p className="text-[#64748b] font-bold text-sm leading-relaxed mb-6 px-2">
              Please scan QR via UPI apps like GPay, PhonePe, etc., and send the transaction screenshot on WhatsApp:
            </p>
            
            {/* WhatsApp Number */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-[16px] py-3 px-4 inline-block">
              <p className="text-emerald-600 font-black tracking-widest">+91 1234567890</p>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ViewAccount;